import React, { useState, useEffect } from 'react';
import { getProducts, createProduct, updateProduct, deleteProduct, bulkImportProducts } from '@/api/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Package, Download, Upload } from 'lucide-react';
import { useActionHistory } from '@/context/ActionHistoryContext';
import * as XLSX from 'xlsx';

const CATEGORIES = ['Peşin Satış', 'Operatör Satışı', 'Arvento Taksitli', 'Proje Satışı', 'Kamera Ürünleri', 'Sensörler', 'M2M', 'Diğer'];

const Products = ({ user }) => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showDialog, setShowDialog] = useState(false);
  const [showImportDialog, setShowImportDialog] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);
  const [categoryFilter, setCategoryFilter] = useState('all');
  const { pushAction } = useActionHistory();
  const [formData, setFormData] = useState({
    name: '',
    purchase_price: '',
    sale_price: '',
    category: 'Diğer',
    for_sales: true,
    for_offers: true
  });

  useEffect(() => {
    loadProducts();
  }, []);

  const loadProducts = async () => {
    try {
      const data = await getProducts();
      setProducts(data);
    } catch (error) {
      toast.error('Ürünler yüklenemedi');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingProduct) {
        await updateProduct(editingProduct.id, {
          name: formData.name,
          purchase_price: parseFloat(formData.purchase_price),
          sale_price: parseFloat(formData.sale_price),
          category: formData.category,
          role: 'admin',
          for_sales: formData.for_sales,
          for_offers: formData.for_offers
        });
        toast.success('Ürün güncellendi');
      } else {
        await createProduct({
          name: formData.name,
          purchase_price: parseFloat(formData.purchase_price),
          sale_price: parseFloat(formData.sale_price),
          category: formData.category,
          role: 'admin',
          for_sales: formData.for_sales,
          for_offers: formData.for_offers
        });
        toast.success('Ürün eklendi');
      }
      setShowDialog(false);
      setEditingProduct(null);
      setFormData({ name: '', purchase_price: '', sale_price: '', category: 'Diğer', for_sales: true, for_offers: true });
      loadProducts();
    } catch (error) {
      toast.error('İşlem başarısız oldu');
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setFormData({
      name: product.name,
      purchase_price: product.purchase_price,
      sale_price: product.sale_price,
      category: product.category || 'Diğer',
      for_sales: product.for_sales !== undefined ? product.for_sales : true,
      for_offers: product.for_offers !== undefined ? product.for_offers : true
    });
    setShowDialog(true);
  };

  const handleDelete = async (productId) => {
    if (!window.confirm('Bu ürünü silmek istediğinizden emin misiniz?')) return;
    
    try {
      await deleteProduct(productId);
      toast.success('Ürün silindi');
      loadProducts();
    } catch (error) {
      toast.error('Ürün silinemedi');
    }
  };

  const handleAddNew = () => {
    setEditingProduct(null);
    setFormData({ name: '', purchase_price: '', sale_price: '', category: 'Diğer' });
    setShowDialog(true);
  };

  const handleExport = () => {
    // Filtre kontrolü - herhangi bir filtre uygulanmış mı?
    const isFiltered = categoryFilter !== 'all';
    
    // Filtre varsa filtrelenmiş veriyi, yoksa tüm veriyi kullan
    const dataToExport = isFiltered ? filteredProducts : products;
    
    const exportData = dataToExport.map(product => ({
      'Ürün Adı': product.name || '',
      'Alış Fiyatı': product.purchase_price || 0,
      'Satış Fiyatı': product.sale_price || 0,
      'Kategori': product.category || 'Diğer',
      'Satışta Göster': product.for_sales ? 'Evet' : 'Hayır',
      'Teklifte Göster': product.for_offers ? 'Evet' : 'Hayır'
    }));

    const ws = XLSX.utils.json_to_sheet(exportData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Ürünler');
    
    const fileName = isFiltered 
      ? `urunler_filtreli_${exportData.length}_adet.xlsx`
      : `tum_urunler_${exportData.length}_adet.xlsx`;
    
    XLSX.writeFile(wb, fileName);
    toast.success(`${exportData.length} ürün Excel'e aktarıldı${isFiltered ? ' (filtrelenmiş)' : ' (tümü)'}`);
  };


  const handleDownloadTemplate = () => {
    const templateData = [
      {
        'Ürün Adı': 'Örnek Ürün 1',
        'Alış Fiyatı': 1000,
        'Satış Fiyatı': 1500,
        'Kategori': 'Peşin Satış'
      },
      {
        'Ürün Adı': 'Örnek Ürün 2',
        'Alış Fiyatı': 2000,
        'Satış Fiyatı': 2800,
        'Kategori': 'M2M'
      }
    ];

    const ws = XLSX.utils.json_to_sheet(templateData);
    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Şablon');
    XLSX.writeFile(wb, 'urun_sablonu.xlsx');
    toast.success('Şablon indirildi');
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    try {
      const reader = new FileReader();
      reader.onload = async (event) => {
        try {
          const data = new Uint8Array(event.target.result);
          const workbook = XLSX.read(data, { type: 'array' });
          const sheetName = workbook.SheetNames[0];
          const worksheet = workbook.Sheets[sheetName];
          const jsonData = XLSX.utils.sheet_to_json(worksheet);

          if (jsonData.length === 0) {
            toast.error('Excel dosyası boş');
            return;
          }

          // Excel verilerini API formatına çevir
          const productsData = jsonData.map(row => ({
            name: row['Ürün Adı'] || row['name'] || '',
            purchase_price: parseFloat(row['Alış Fiyatı'] || row['purchase_price'] || 0),
            sale_price: parseFloat(row['Satış Fiyatı'] || row['sale_price'] || 0),
            category: row['Kategori'] || row['category'] || 'Diğer',
            role: 'admin'
          }));

          // Backend'e gönder
          const result = await bulkImportProducts(productsData);

          if (result.success_count > 0) {
            toast.success(`${result.success_count} ürün başarıyla içe aktarıldı`);
            if (result.error_count > 0) {
              toast.warning(`${result.error_count} ürün aktarılamadı`);
              console.error('Import errors:', result.errors);
            }
            loadProducts();
            setShowImportDialog(false);
          } else {
            toast.error('Hiçbir ürün aktarılamadı');
            if (result.errors && result.errors.length > 0) {
              console.error('Import errors:', result.errors);
            }
          }
        } catch (error) {
          console.error('Import error:', error);
          toast.error('İçe aktarma sırasında hata oluştu');
        }
      };
      reader.readAsArrayBuffer(file);
    } catch (error) {
      console.error('File read error:', error);
      toast.error('Dosya okunamadı');
    }
  };



  // Filtered and grouped products
  const filteredProducts = categoryFilter === 'all' 
    ? products 
    : products.filter(p => p.category === categoryFilter);

  // Group products by category
  const groupedProducts = filteredProducts.reduce((acc, product) => {
    const category = product.category || 'Diğer';
    if (!acc[category]) acc[category] = [];
    acc[category].push(product);
    return acc;
  }, {});

  // Kategori sırasını belirle
  const categoryOrder = [
    "Peşin Satış",
    "Diğer",
    "Operatör Satışı",
    "Arvento Taksitli",
    "M2M",
    "Sensörler",
    "Kamera Ürünleri"
  ];

  // Sıralı kategorileri oluştur
  const sortedGroupedProducts = {};
  categoryOrder.forEach(cat => {
    if (groupedProducts[cat]) {
      sortedGroupedProducts[cat] = groupedProducts[cat];
    }
  });
  
  // Tanımsız kategorileri sona ekle
  Object.entries(groupedProducts).forEach(([cat, products]) => {
    if (!categoryOrder.includes(cat)) {
      sortedGroupedProducts[cat] = products;
    }
  });

  if (loading) return <div className="p-6">Yükleniyor...</div>;

  return (
    <div className="p-3 sm:p-6 space-y-4 sm:space-y-6">
      {/* Header - Mobil Responsive */}
      <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-3">
        <div>
          <h1 className="text-2xl sm:text-4xl font-bold text-gray-800 mb-1 sm:mb-2">Ürün Yönetimi</h1>
          <p className="text-sm sm:text-base text-gray-600">Toplam {products.length} ürün</p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button onClick={() => setShowImportDialog(true)} variant="outline" className="flex-1 sm:flex-none">
            <Upload className="w-4 h-4 mr-2" />
            İçeri Aktar
          </Button>
          <Button onClick={handleExport} variant="outline" className="flex-1 sm:flex-none">
            <Download className="w-4 h-4 mr-2" />
            Dışarı Aktar
          </Button>
          <Button onClick={handleAddNew} className="bg-blue-600 hover:bg-blue-700 text-white flex-1 sm:flex-none">
            <Plus className="w-4 h-4 mr-2" />
            Yeni Ürün Ekle
          </Button>
        </div>
      </div>

      {/* Category Summary - Mobil 2 sütun */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 p-3 sm:p-4 rounded-lg border-2 border-blue-200">
        <h3 className="text-base sm:text-lg font-bold text-gray-800 mb-3">📊 Kategori Özeti</h3>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-2 sm:gap-3">
          {Object.entries(sortedGroupedProducts).map(([category, categoryProducts]) => (
            <button
              key={category}
              onClick={() => {
                const oldValue = categoryFilter;
                setCategoryFilter(category);
                pushAction({
                  undo: () => setCategoryFilter(oldValue)
                });
              }}
              className={`p-2 sm:p-3 rounded-lg border-2 transition-all hover:shadow-lg ${
                categoryFilter === category 
                  ? 'bg-blue-600 text-white border-blue-700 shadow-lg' 
                  : 'bg-white text-gray-700 border-gray-200 hover:border-blue-400'
              }`}
            >
              <div className="font-bold text-xs sm:text-sm line-clamp-2">{category}</div>
              <div className="text-xl sm:text-2xl font-bold mt-1">{categoryProducts.length}</div>
              <div className="text-xs opacity-75">ürün</div>
            </button>
          ))}
        </div>
      </div>

      {/* Category Filter - Mobil responsive */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-2 sm:space-x-3 bg-white p-3 sm:p-4 rounded-lg shadow">
        <Label className="text-sm font-medium">Kategori Filtresi:</Label>
        <Select value={categoryFilter} onValueChange={(newValue) => {
          const oldValue = categoryFilter;
          setCategoryFilter(newValue);
          pushAction({
            undo: () => setCategoryFilter(oldValue)
          });
        }}>
          <SelectTrigger className="w-full sm:w-64">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tüm Kategoriler</SelectItem>
            {CATEGORIES.map(cat => (
              <SelectItem key={cat} value={cat}>{cat}</SelectItem>
            ))}
          </SelectContent>
        </Select>
        <span className="text-sm text-gray-500">
          ({filteredProducts.length} ürün)
        </span>
        {categoryFilter !== 'all' && (
          <Button 
            variant="outline" 
            size="sm" 
            onClick={() => {
              const oldValue = categoryFilter;
              setCategoryFilter('all');
              pushAction({
                undo: () => setCategoryFilter(oldValue)
              });
            }}
            className="w-full sm:w-auto sm:ml-2"
          >
            Tümünü Göster
          </Button>
        )}
      </div>

      {/* Products - Mobil Card / Desktop Table */}
      <div className="space-y-4 sm:space-y-6">
        {Object.entries(sortedGroupedProducts).map(([category, categoryProducts]) => (
          <div key={category} className="bg-white rounded-lg sm:rounded-xl shadow-lg overflow-hidden">
            <div className="bg-gradient-to-r from-blue-500 to-blue-600 text-white px-3 sm:px-6 py-2 sm:py-3">
              <h2 className="text-base sm:text-xl font-bold flex items-center">
                <Package className="w-4 h-4 sm:w-5 sm:h-5 mr-2" />
                {category} ({categoryProducts.length})
              </h2>
            </div>
            
            {/* Mobile Card View */}
            <div className="md:hidden divide-y">
              {categoryProducts.map((product) => (
                <div key={product.id} className="p-3">
                  <div className="flex justify-between items-start mb-2">
                    <h3 className="font-bold text-gray-900 flex-1">{product.name}</h3>
                  </div>
                  <div className="grid grid-cols-2 gap-2 text-sm mb-2">
                    <div>
                      <span className="text-gray-500">Alış:</span>
                      <div className="font-medium">{product.purchase_price.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</div>
                    </div>
                    <div>
                      <span className="text-gray-500">Satış:</span>
                      <div className="font-medium">{product.sale_price.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺</div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between pt-2 border-t">
                    <div>
                      <span className="text-xs text-gray-500">Puan:</span>
                      <span className={`ml-1 font-bold ${product.profit >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                        {product.profit.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                      </span>
                    </div>
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleEdit(product)}
                      >
                        <Pencil className="w-4 h-4" />
                      </Button>
                      {user?.role === 'admin' && (
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-600 hover:bg-red-50"
                          onClick={() => handleDelete(product.id)}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table View */}
            <div className="hidden md:block overflow-x-auto">
              <table className="w-full">
                <thead className="bg-gray-50 border-b">
                  <tr>
                    <th className="px-4 py-3 text-left text-sm font-semibold">Ürün Adı</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">Alış Fiyatı</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">Satış Fiyatı</th>
                    <th className="px-4 py-3 text-right text-sm font-semibold">Puan</th>
                    <th className="px-4 py-3 text-center text-sm font-semibold">İşlemler</th>
                  </tr>
                </thead>
                <tbody>
                  {categoryProducts.map((product, index) => (
                    <tr key={product.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                      <td className="px-4 py-3">
                        <span className="font-medium">{product.name}</span>
                      </td>
                      <td className="px-4 py-3 text-right">
                        {product.purchase_price.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                      </td>
                      <td className="px-4 py-3 text-right">
                        {product.sale_price.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                      </td>
                      <td className="px-4 py-3 text-right">
                        <span className={product.profit >= 0 ? 'text-green-600 font-semibold' : 'text-red-600 font-semibold'}>
                          {product.profit.toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                        </span>
                      </td>
                      <td className="px-4 py-3 text-center">
                        <div className="flex justify-center space-x-2">
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleEdit(product)}
                          >
                            <Pencil className="w-4 h-4" />
                          </Button>
                          {user?.role === 'admin' && (
                            <Button
                              size="sm"
                              variant="outline"
                              className="text-red-600 hover:bg-red-50"
                              onClick={() => handleDelete(product.id)}
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        ))}
      </div>

      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingProduct ? 'Ürün Düzenle' : 'Yeni Ürün Ekle'}</DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label>Ürün Adı</Label>
              <Input
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Alış Fiyatı (₺)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.purchase_price}
                onChange={(e) => setFormData({ ...formData, purchase_price: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Satış Fiyatı (₺)</Label>
              <Input
                type="number"
                step="0.01"
                value={formData.sale_price}
                onChange={(e) => setFormData({ ...formData, sale_price: e.target.value })}
                required
              />
            </div>
            <div>
              <Label>Kategori</Label>
              <Select 
                value={formData.category} 
                onValueChange={(value) => setFormData({ ...formData, category: value })}
              >
                <SelectTrigger>
                  <SelectValue placeholder="Kategori seçiniz" />
                </SelectTrigger>
                <SelectContent>
                  {CATEGORIES.map(cat => (
                    <SelectItem key={cat} value={cat}>{cat}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            {user?.role === 'admin' && (
              <div className="space-y-4 p-4 bg-blue-50 rounded-lg border-2 border-blue-300">
                <Label className="text-base font-bold text-blue-900">📋 Görünürlük Ayarları</Label>
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="for_sales"
                    checked={formData.for_sales}
                    onCheckedChange={(checked) => setFormData({ ...formData, for_sales: checked })}
                  />
                  <div className="grid gap-1.5 leading-none">
                    <label
                      htmlFor="for_sales"
                      className="text-sm font-medium text-gray-900 cursor-pointer"
                    >
                      🛒 Satışlarda Göster
                    </label>
                    <p className="text-xs text-gray-600">Bu ürün satış ekranında listelensin mi?</p>
                  </div>
                </div>
                <div className="flex items-start space-x-3">
                  <Checkbox
                    id="for_offers"
                    checked={formData.for_offers}
                    onCheckedChange={(checked) => setFormData({ ...formData, for_offers: checked })}
                  />
                  <div className="grid gap-1.5 leading-none">
                    <label
                      htmlFor="for_offers"
                      className="text-sm font-medium text-gray-900 cursor-pointer"
                    >
                      📄 Tekliflerde Göster
                    </label>
                    <p className="text-xs text-gray-600">Bu ürün teklif ekranında listelensin mi?</p>
                  </div>
                </div>
              </div>
            )}
            {formData.purchase_price && formData.sale_price && (
              <div className="p-3 bg-gray-100 rounded">
                <p className="text-sm text-gray-600">Puan Marjı:</p>
                <p className={`text-lg font-bold ${(parseFloat(formData.sale_price) - parseFloat(formData.purchase_price)) >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                  {(parseFloat(formData.sale_price) - parseFloat(formData.purchase_price)).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ₺
                </p>
              </div>
            )}
            <div className="flex justify-end space-x-2">
              <Button type="button" variant="outline" onClick={() => setShowDialog(false)}>
                İptal
              </Button>
              <Button type="submit" className="progo-gradient">
                {editingProduct ? 'Güncelle' : 'Ekle'}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>


      {/* Import Dialog */}
      <Dialog open={showImportDialog} onOpenChange={setShowImportDialog}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Ürün İçe Aktarma</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-blue-50 p-4 rounded-lg border border-blue-200">
              <h4 className="font-semibold text-blue-900 mb-2">📋 Şablon İndir</h4>
              <p className="text-sm text-blue-800 mb-3">
                Önce şablonu indirin, ürünlerinizi doldurun, sonra yükleyin.
              </p>
              <Button 
                onClick={handleDownloadTemplate} 
                variant="outline" 
                className="w-full border-blue-300 hover:bg-gray-50"
              >
                <Download className="w-4 h-4 mr-2" />
                Excel Şablonunu İndir
              </Button>
            </div>

            <div className="bg-green-50 p-4 rounded-lg border border-green-200">
              <h4 className="font-semibold text-green-900 mb-2">📤 Dosya Yükle</h4>
              <p className="text-sm text-green-800 mb-3">
                Doldurduğunuz Excel dosyasını seçin.
              </p>
              <Input
                type="file"
                accept=".xlsx,.xls"
                onChange={handleImport}
                className="cursor-pointer"
              />
            </div>

            <div className="bg-yellow-50 p-3 rounded-lg border border-yellow-200">
              <h4 className="font-semibold text-yellow-900 text-sm mb-1">⚠️ Önemli Notlar:</h4>
              <ul className="text-xs text-yellow-800 space-y-1">
                <li>• Ürün Adı zorunludur</li>
                <li>• Fiyatlar sayı olmalıdır</li>
                <li>• Aynı isimli ürünler güncellenecektir</li>
                <li>• Kategori: Peşin Satış, M2M, Kamera Ürünleri, Sensörler, Diğer</li>
              </ul>
            </div>
          </div>
        </DialogContent>
      </Dialog>

    </div>
  );
};

export default Products;
